
// Enhanced CRM Member type with comprehensive business data
import { MemberStatus } from './memberStatus';

// New interface for member_types table
export interface MemberTypeRecord {
  id: string;
  member_id: string;
  type: string;
  is_primary: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Member {
  id: string;
  user_id?: string;  // Optional for form creation
  firstname?: string;  // Database column name (primary)
  lastname?: string;   // Database column name (primary)
  email: string;  // NOT NULL in DB
  company?: string;
  city?: string;
  country?: string;
  image?: string;
  role?: string;
  type?: string;  // Optional for forms, has default in DB
  
  // Multiple types support
  member_types?: MemberTypeRecord[];  // Types from member_types table
  
  // Legacy compatibility fields (computed from database fields)
  name?: string;      // Computed from firstname + lastname
  
  // Admin-specific fields for club management (optional for forms)
  status?: string;  // Has default 'actif' in DB
  subscription_type?: string;  // Has default 'basic' in DB
  is_visible_in_directory?: boolean;  // Visibility control for directory
  is_team_member?: boolean;  // Indicates if the member is part of the First Tuesday team
  
  // Date fields
  join_date?: string;
  last_payment?: string;
  
  // CRM Lead Management
  lead_source?: string;
  lead_status?: 'new' | 'contacted' | 'qualified' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost' | string;
  lead_score?: number;
  last_interaction?: string;
  next_follow_up?: string;
  
  // CRM Business Data
  lifetime_value?: number;
  acquisition_cost?: number;
  referral_source?: string;
  industry?: string;
  company_size?: 'startup' | 'sme' | 'enterprise' | 'corporation' | 'self_employed' | string;
  decision_maker?: boolean;
  budget_range?: string;
  
  // Rich profile data
  gender?: string;
  languages?: string[];
  achievements?: string;
  needs?: string[];
  offers?: string[];  // Ce que le membre propose
  tags?: string[];
  skills?: string[];
  interests?: string[];
  bio?: string;
  
  // Contact info
  phone?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

// Legacy type alias for compatibility
export type MemberProps = Member;

export type MemberType = 'membre' | 'entrepreneur' | 'investisseur' | 'expert' | 'consultant' | 'startup' | 'journaliste' | 'influenceur' | 'gouvernement' | 'association' | 'chercheur' | 'autre';

export interface MemberFilterOptions {
  searchTerm: string;
  country: string;
  sector: string;
  role: string;
  type: string;
  language: string;
  skill: string;
}

export interface AdminMemberFilterOptions {
  searchTerm: string;
  status: string;
  subscription_type: string;
  country: string;
}

// Mapping functions for Supabase compatibility
export const mapMemberToSupabase = (member: Member): any => {
  // Define allowed database columns to prevent sending invalid fields
  const allowedFields = [
    'id', 'user_id', 'firstname', 'lastname', 'email', 'company', 'city', 'country', 
    'image', 'role', 'type', 'status', 'subscription_type', 'is_visible_in_directory', 'is_team_member',
    'lead_source', 'lead_status', 'lead_score', 'last_interaction', 'next_follow_up',
    'lifetime_value', 'acquisition_cost', 'referral_source', 'industry', 'company_size',
    'decision_maker', 'budget_range', 'gender', 'languages', 'achievements', 'needs', 'offers', 'tags', 
    'skills', 'interests', 'bio', 'phone', 'linkedin', 'twitter', 'website', 'created_at', 'updated_at'
  ];

  // Filter member data to only include allowed fields with values
  const filteredMember: any = {};
  Object.keys(member).forEach(key => {
    if (allowedFields.includes(key)) {
      const value = (member as any)[key];
      // Skip empty string IDs for new members
      if (key === 'id' && (!value || value === '')) {
        return;
      }
      filteredMember[key] = value;
    }
  });

  // Handle field name mapping and compute name if missing
  if (!filteredMember.firstname && member.name) {
    filteredMember.firstname = member.name.split(' ')[0] || '';
  }
  if (!filteredMember.lastname && member.name) {
    filteredMember.lastname = member.name.split(' ').slice(1).join(' ') || '';
  }
  
  return filteredMember;
};

export const mapSupabaseToMember = (record: any): Member => {
  const { firstname, lastname, ...rest } = record;
  
  return {
    ...rest,
    // Use database column names as primary
    firstname,
    lastname,
    
    // Computed fields for display
    name: firstname && lastname ? `${firstname} ${lastname}` : (firstname || lastname || ''),
  };
};
